import { useEffect, useState } from 'react';
import { api, sotd as Api, type SotdInput, type SotdNote, type Stance, type WatchRow } from '@tvx/api-client';
import { Modal } from '../components/Modal';
import { ImagePick } from '../components/ImagePick';

const today = () => new Date().toISOString().slice(0, 10);

export function SotdForm({ note, onCancel, onSaved }: {
  note: SotdNote | null;
  onCancel: () => void;
  onSaved: () => Promise<void>;
}) {
  const [f, setF] = useState<SotdInput>(() => note ? {
    note_date: note.note_date, headline: note.headline, note: note.note, support: note.support,
    pivot: note.pivot, resistance: note.resistance, chart_media_id: note.chart_media_id,
    state: note.state, watchlist: note.watchlist?.length ? note.watchlist : [blankRow()],
  } : {
    note_date: today(), headline: '', note: '', support: '', pivot: '', resistance: '',
    chart_media_id: null, state: 'draft', watchlist: [blankRow()],
  });
  const [chart, setChart] = useState<string | null>(note?.chart_path ?? null);
  const [stances, setStances] = useState<Stance[]>(['Long', 'Avoid', 'Watch', 'Neutral']);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  /* The list does not carry the watchlist rows — only a count — so a note
     opened from the list has to fetch itself. Saving without this would post
     an empty watchlist and the server replaces the rows wholesale: editing a
     headline would silently delete the stocks under it. */
  const [loadingRows, setLoadingRows] = useState(!!note);

  useEffect(() => {
    void api.get<{ stances: Stance[] }>('/meta').then((m) => m.stances && setStances(m.stances)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!note) return;
    let alive = true;
    Api.get(note.id)
      .then(({ note: full }) => {
        if (!alive) return;
        setF((p) => ({ ...p, watchlist: full.watchlist?.length ? full.watchlist : [blankRow()] }));
      })
      .catch(() => setError('Could not load this note’s watchlist — save would clear it, so edit is disabled.'))
      .finally(() => alive && setLoadingRows(false));
    return () => { alive = false; };
  }, [note?.id]);          // eslint-disable-line react-hooks/exhaustive-deps

  const set = <K extends keyof SotdInput>(k: K, v: SotdInput[K]) => setF((p) => ({ ...p, [k]: v }));
  const setRow = (i: number, patch: Partial<WatchRow>) =>
    setF((p) => ({ ...p, watchlist: p.watchlist.map((r, n) => (n === i ? { ...r, ...patch } : r)) }));

  async function submit(state: 'draft' | 'published') {
    if (loadingRows) { setError('Still loading this note — give it a moment.'); return; }
    if (!f.headline.trim()) { setError('A headline is required.'); return; }
    setError('');
    setStatus(state === 'published' ? 'Publishing…' : 'Saving…');
    try {
      /* Blank rows are how somebody leaves the last line alone, not an
         instruction to store an empty stock. */
      const body = { ...f, state, watchlist: f.watchlist.filter((r) => r.symbol.trim()) };
      if (note) await Api.update(note.id, body);
      else await Api.create(body);
      await onSaved();
    } catch (e) { setStatus(''); setError((e as Error).message); }
  }

  return (
    <Modal
      title={note ? `Edit note · ${note.note_date}` : 'New morning note'}
      onClose={onCancel}
      footer={
        <>
          <span style={{ fontSize: 12, color: 'var(--tx3)' }}>{status}</span>
          <span className="mdl-spacer" />
          <button className="b bo" type="button" onClick={onCancel}>Cancel</button>
          <button className="b bo" type="button" onClick={() => void submit('draft')}>Save draft</button>
          <button className="b bg" type="button" onClick={() => void submit('published')}>
            {note?.state === 'published' ? 'Save' : 'Publish'}
          </button>
        </>
      }
    >
      {error ? <div className="err">{error}</div> : null}

      <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: 14 }}>
        <Field label="Date">
          <input className="inp" type="date" value={f.note_date}
            onChange={(e) => set('note_date', e.target.value)} />
        </Field>
        <Field label="Headline">
          <input className="inp" value={f.headline} autoFocus
            onChange={(e) => set('headline', e.target.value)} />
        </Field>
      </div>

      <Field label="The note (members read this)">
        <textarea className="inp ta" rows={5} value={f.note} onChange={(e) => set('note', e.target.value)} />
      </Field>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 14 }}>
        <Field label="Support"><input className="inp" value={f.support} onChange={(e) => set('support', e.target.value)} /></Field>
        <Field label="Pivot"><input className="inp" value={f.pivot} onChange={(e) => set('pivot', e.target.value)} /></Field>
        <Field label="Resistance"><input className="inp" value={f.resistance} onChange={(e) => set('resistance', e.target.value)} /></Field>
      </div>

      <ImagePick label="Chart" path={chart} alt={f.headline} ratio="16 / 9"
        hint="The marked-up chart that goes with the note. Members see it above the levels."
        onPick={(id, p) => { set('chart_media_id', id); setChart(p); }} />

      <div className="fld">
        <label className="lbl">Watchlist{loadingRows ? ' — loading…' : ''}</label>
        {f.watchlist.map((row, i) => (
          <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 140px 1fr 34px', gap: 8, marginBottom: 8 }}>
            <input className="inp" placeholder="SYMBOL" value={row.symbol}
              onChange={(e) => setRow(i, { symbol: e.target.value.toUpperCase() })} />
            <select className="inp" value={row.stance} onChange={(e) => setRow(i, { stance: e.target.value as Stance })}>
              {stances.map((s) => <option key={s}>{s}</option>)}
            </select>
            <input className="inp" placeholder="Level or note" value={row.level}
              onChange={(e) => setRow(i, { level: e.target.value })} />
            <button className="b bo" type="button" style={{ padding: 0 }} aria-label="Remove"
              onClick={() => setF((p) => ({
                ...p,
                watchlist: p.watchlist.length === 1 ? [blankRow()] : p.watchlist.filter((_, n) => n !== i),
              }))}>×</button>
          </div>
        ))}
        <button className="b bo" type="button" style={{ padding: '7px 12px', fontSize: 12 }}
          onClick={() => setF((p) => ({ ...p, watchlist: [...p.watchlist, blankRow()] }))}>Add a stock</button>
      </div>
    </Modal>
  );
}

const blankRow = (): WatchRow => ({ symbol: '', stance: 'Watch', level: '' });

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <div className="fld"><label className="lbl">{label}</label>{children}</div>;
}
